import type { ReactNode } from 'react'
import clsx from 'clsx'
import { CheckCircle, AlertErrorCircle, WarningTriangle, XCircle, Retry, Clock } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { IconButton } from '@/components/icon-button/IconButton'
import { UploadCardStatus } from '@/components/upload-card/constants'
import { UploadCardFileIcon } from '@/components/upload-card/UploadCardFileIcon'

export interface UploadCardProps {
  /** Current status of the upload */
  status: UploadCardStatus
  /** Display filename (e.g. "Document Name.PDF") */
  filename: string
  /** Optional confirmation displayed alongside the current upload status. */
  statusPrefix?: string
  /** Status message for Complete / Error / Warning / Queued. Complete defaults to "Completed". */
  message?: string | ReactNode
  /** Called when user clicks Cancel (Uploading only) */
  onCancel?: () => void
  /** When provided, shows dismiss (X) for Processing / Queued / Complete / Error / Warning. */
  onDismiss?: () => void
  /** Accessible name for the dismiss control. Defaults to "Dismiss <filename>". */
  dismissLabel?: string
  /** When provided and status is Error, shows Retry button. Called when user clicks it. */
  onRetry?: () => void
  /**
   * Makes the filename area activatable — e.g. opening the file in the content viewer. The action
   * buttons stay outside it, so they are never nested inside another interactive element.
   */
  onRowClick?: () => void
}

const defaultCompleteMessage = 'Completed'

export function UploadCard({ status, filename, message, statusPrefix, onCancel, onDismiss, dismissLabel, onRetry, onRowClick }: UploadCardProps) {
  const isComplete = status === UploadCardStatus.Complete
  const isError = status === UploadCardStatus.Error
  const isPending = status === UploadCardStatus.Pending
  const isUploadPending = status === UploadCardStatus.UploadPending
  const isWarning = status === UploadCardStatus.Warning
  const isUploading = status === UploadCardStatus.Uploading
  const isProcessing = status === UploadCardStatus.Processing
  const isQueued = status === UploadCardStatus.Queued
  const isLoading = isUploading || isPending || isUploadPending || isProcessing
  const showsDismiss = isProcessing || isQueued || isComplete || isError || isWarning

  const statusMessage = message ?? (isComplete ? defaultCompleteMessage : undefined)

  const rowBody = (
    <>
      <div className={clsx('border-r border-solid border-border-default', 'flex h-full items-center justify-center px-3 rounded-l-[5px] shrink-0 w-[60px]')}>
        {isQueued ? (
          <Clock className="h-6 w-6 shrink-0 text-text-tertiary" role="img" aria-label="Queued" />
        ) : isLoading ? (
          <CircularProgressContinuousSized size={24} thickness={5} showTrack />
        ) : (
          <UploadCardFileIcon filename={filename} error={isError} />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 items-start justify-center min-w-0">
        <p className="min-w-0 max-w-full text-sm font-medium leading-normal text-text-primary truncate">{filename}</p>
        <div className="flex min-w-0 w-full items-center gap-1">
          {statusPrefix && <span className="shrink-0 text-xs text-text-tertiary">{statusPrefix} ·</span>}
          {isUploading && <span className="min-w-0 flex-1 truncate text-xs text-text-tertiary italic">Uploading...</span>}

          {isUploadPending && <span className="min-w-0 flex-1 truncate text-xs text-text-tertiary italic">Waiting to upload...</span>}

          {isPending && <span className="min-w-0 flex-1 truncate text-xs text-text-tertiary italic">Pending...</span>}

          {isProcessing && <span className="min-w-0 flex-1 truncate text-xs text-text-tertiary italic">Processing...</span>}
          {isQueued && <span className="min-w-0 flex-1 truncate text-xs text-text-tertiary">{message ?? 'Queued'}</span>}

          {isComplete && (
            <>
              <CheckCircle className="w-3.5 h-3.5 shrink-0 text-green-800" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-xs text-green-800">{statusMessage}</span>
            </>
          )}

          {isError && (
            <>
              <AlertErrorCircle className="w-3.5 h-3.5 shrink-0 text-red-800" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-xs text-red-800">{statusMessage}</span>
            </>
          )}

          {isWarning && (
            <>
              <WarningTriangle className="w-3.5 h-3.5 shrink-0 text-yellow-800" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-xs text-yellow-800">{statusMessage}</span>
            </>
          )}
        </div>
      </div>
    </>
  )

  return (
    <div
      className={clsx(
        'flex gap-2.5 h-[60px] items-center rounded-[5px] w-full min-w-0 max-w-full pr-3',
        'border border-border-default border-solid',
        isWarning && 'bg-yellow-50',
        !isWarning && 'bg-surface-emphasis',
        // Only an activatable row gets a hover treatment; a static row must not look clickable.
        onRowClick && 'hover:bg-surface-hover',
        'transition-colors'
      )}
    >
      {/* When the row is activatable it becomes its own button, so the action buttons below stay
          siblings rather than nesting inside another interactive element. */}
      {onRowClick ? (
        <button type="button" onClick={onRowClick} className="flex h-full min-w-0 flex-1 items-center gap-2.5 rounded-l-[5px] text-left">
          {rowBody}
        </button>
      ) : (
        rowBody
      )}

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {onCancel && (
          <Button variant="plain" color="destructive" size="xs" className="shrink-0" onClick={onCancel}>
            Cancel
          </Button>
        )}

        {isError && onRetry && (
          <Button variant="plain" color="secondary" size="xs" leadingIcon={<Retry className="w-3.5 h-3.5 shrink-0" />} onClick={onRetry}>
            Retry
          </Button>
        )}

        {showsDismiss && onDismiss && (
          <IconButton
            variant="plain"
            color="neutral"
            size="md"
            icon={<XCircle className="w-5 h-5" />}
            aria-label={dismissLabel ?? `Dismiss ${filename}`}
            onClick={onDismiss}
          />
        )}
      </div>
    </div>
  )
}
